/**
 * /settings/pos/stock — central stock dashboard.
 *
 * Shows:
 *   - Low-stock alerts at the top (products at or below reorder_threshold)
 *   - Product list with current stock_quantity + action buttons
 *   - Recent movements table (last 50)
 *
 * Restock / Adjust / Wastage modals are centered overlays.
 *
 * Gate: requires pos.session.read for viewing (reads are open to all staff
 * who can use the POS). Restock/adjust/wastage require pos.stock.write.
 */
import {
  ApiError,
  getPosStockAlerts,
  listPosProducts,
  listPosStockMovements,
  posProductAdjust,
  posProductRestock,
  posProductWastage,
} from '@/lib/api';
import type { PosProduct, PosStockMovement } from '@/lib/types';
import { AlertTriangle, BarChart3, RefreshCw, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function movementTypeLabel(type: string): string {
  const map: Record<string, string> = {
    purchase: 'Restock',
    sale: 'Sale',
    adjustment: 'Adjustment',
    wastage: 'Wastage',
    return: 'Return',
    initial: 'Initial',
  };
  return map[type] ?? type;
}

function movementTypeClass(type: string): string {
  if (type === 'purchase' || type === 'return' || type === 'initial') return 'text-success';
  if (type === 'wastage') return 'text-destructive';
  if (type === 'sale') return 'text-muted-foreground';
  return 'text-foreground';
}

// ---------------------------------------------------------------------------
// Restock modal
// ---------------------------------------------------------------------------

interface RestockModalProps {
  product: PosProduct;
  onClose: () => void;
  onSaved: () => void;
}

function RestockModal({ product, onClose, onSaved }: RestockModalProps) {
  const [qty, setQty] = useState('');
  const [costText, setCostText] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const qtyNum = Number(qty);
    if (!Number.isInteger(qtyNum) || qtyNum <= 0) {
      setError('Quantity must be a positive whole number');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const costMinor = costText.trim() !== '' ? Math.round(Number(costText) * 100) : undefined;
      await posProductRestock(product.id, {
        qty: qtyNum,
        unit_cost_minor: costMinor ?? null,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-border">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Restock — {product.name}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="restock-qty"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Quantity to add <span className="text-destructive">*</span>
            </label>
            <input
              id="restock-qty"
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="20"
            />
          </div>
          <div>
            <label
              htmlFor="restock-cost"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Unit cost (optional)
            </label>
            <input
              id="restock-cost"
              type="number"
              step="0.01"
              min="0"
              value={costText}
              onChange={(e) => setCostText(e.target.value)}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="5.99"
            />
          </div>
          <div>
            <label
              htmlFor="restock-notes"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Notes (optional)
            </label>
            <input
              id="restock-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Weekly delivery"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Add stock'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjust modal
// ---------------------------------------------------------------------------

interface AdjustModalProps {
  product: PosProduct;
  onClose: () => void;
  onSaved: () => void;
}

function AdjustModal({ product, onClose, onSaved }: AdjustModalProps) {
  const [changeQty, setChangeQty] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const delta = Number(changeQty);
    if (!Number.isInteger(delta) || delta === 0) {
      setError('Change quantity must be a non-zero integer');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await posProductAdjust(product.id, {
        change_qty: delta,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-border">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Adjust stock — {product.name}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="adjust-qty"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Change quantity <span className="text-destructive">*</span>
            </label>
            <input
              id="adjust-qty"
              type="number"
              step="1"
              value={changeQty}
              onChange={(e) => setChangeQty(e.target.value)}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="-3 or +5"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Positive to increase, negative to decrease
            </p>
          </div>
          <div>
            <label
              htmlFor="adjust-notes"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Notes (optional)
            </label>
            <input
              id="adjust-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Stock count correction"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Apply adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wastage modal
// ---------------------------------------------------------------------------

interface WastageModalProps {
  product: PosProduct;
  onClose: () => void;
  onSaved: () => void;
}

function WastageModal({ product, onClose, onSaved }: WastageModalProps) {
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    const qtyNum = Number(qty);
    if (!Number.isInteger(qtyNum) || qtyNum <= 0) {
      setError('Quantity must be a positive whole number');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await posProductWastage(product.id, {
        qty: qtyNum,
        reason: reason.trim() || null,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface p-6 shadow-xl ring-1 ring-border">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-heading text-lg font-medium">Write off wastage — {product.name}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground press"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="wastage-qty"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Quantity to write off <span className="text-destructive">*</span>
            </label>
            <input
              id="wastage-qty"
              type="number"
              min="1"
              step="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="num w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="2"
            />
          </div>
          <div>
            <label
              htmlFor="wastage-reason"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Reason (optional)
            </label>
            <input
              id="wastage-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              placeholder="Expired, damaged, etc."
            />
          </div>
          <div>
            <label
              htmlFor="wastage-notes"
              className="mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground"
            >
              Notes (optional)
            </label>
            <input
              id="wastage-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground press"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSave()}
            className="rounded-md bg-destructive px-3 py-1.5 text-sm font-medium text-white press disabled:opacity-50"
          >
            {busy ? 'Saving…' : 'Write off'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type ModalState =
  | { kind: 'none' }
  | { kind: 'restock'; product: PosProduct }
  | { kind: 'adjust'; product: PosProduct }
  | { kind: 'wastage'; product: PosProduct };

export function PosStockSettings() {
  const [trackedProducts, setTrackedProducts] = useState<PosProduct[]>([]);
  const [alerts, setAlerts] = useState<Array<{ product: PosProduct; deficit: number }>>([]);
  const [movements, setMovements] = useState<PosStockMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [modal, setModal] = useState<ModalState>({ kind: 'none' });

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const [productsRes, alertsRes, movementsRes] = await Promise.all([
        listPosProducts(false), // include inactive
        getPosStockAlerts(),
        listPosStockMovements({ limit: 50 }),
      ]);
      setTrackedProducts(productsRes.products.filter((p) => p.stockTrackingEnabled === 1));
      setAlerts(alertsRes.alerts);
      setMovements(movementsRes.movements);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 402)) {
        setPermissionDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function handleSaved() {
    setModal({ kind: 'none' });
    void refresh();
  }

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to view the stock dashboard
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access, or upgrade to a Pro plan.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-medium tracking-tight">Stock management</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Track inventory levels for retail products. Enable stock tracking on individual products
            in the Products settings page.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm text-muted-foreground hover:text-foreground press"
        >
          <RefreshCw className="size-4" strokeWidth={1.75} />
          Refresh
        </button>
      </header>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-14 animate-pulse rounded-lg bg-secondary"
              style={{ opacity: 1 - i * 0.25 }}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {/* Low-stock alerts */}
          {alerts.length > 0 && (
            <section>
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle className="size-4 text-warning" strokeWidth={1.75} />
                <h2 className="text-[11px] font-medium uppercase tracking-[0.14em] text-warning">
                  Low stock alerts ({alerts.length})
                </h2>
              </div>
              <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
                <div className="divide-y divide-border">
                  {alerts.map(({ product, deficit }) => (
                    <div key={product.id} className="flex items-center justify-between px-5 py-3.5">
                      <div>
                        <span className="text-sm font-medium">{product.name}</span>
                        {product.sku && (
                          <span className="ml-2 num text-xs text-muted-foreground">
                            {product.sku}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="num text-destructive font-medium">
                          {product.stockQuantity} in stock
                        </span>
                        <span className="num text-muted-foreground">Reorder {deficit} units</span>
                        <button
                          type="button"
                          onClick={() => setModal({ kind: 'restock', product })}
                          className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground press"
                        >
                          Restock
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Tracked products list */}
          <section>
            <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Tracked products
            </h2>
            {trackedProducts.length === 0 ? (
              <div className="rounded-2xl bg-surface p-10 text-center ring-1 ring-border elevate">
                <BarChart3
                  className="mx-auto mb-3 size-6 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <p className="text-sm font-medium text-foreground">No stock-tracked products.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Enable stock tracking on products at{' '}
                  <a href="/settings/pos/products" className="text-primary hover:underline">
                    /settings/pos/products
                  </a>
                  .
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
                <div className="grid grid-cols-[1.5fr_6rem_6rem_auto] gap-3 border-b border-border px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <span>Product</span>
                  <span>In stock</span>
                  <span>Reorder at</span>
                  <span>Actions</span>
                </div>
                <div className="divide-y divide-border">
                  {trackedProducts.map((product) => {
                    const isLow = product.stockQuantity <= product.reorderThreshold;
                    return (
                      <div
                        key={product.id}
                        className="grid grid-cols-[1.5fr_6rem_6rem_auto] items-center gap-3 px-5 py-3.5 text-sm"
                      >
                        <div>
                          <span className="font-medium">{product.name}</span>
                          {product.sku && (
                            <span className="ml-2 num text-xs text-muted-foreground">
                              {product.sku}
                            </span>
                          )}
                        </div>
                        <span
                          className={`num font-medium ${isLow ? 'text-destructive' : 'text-foreground'}`}
                        >
                          {product.stockQuantity}
                        </span>
                        <span className="num text-muted-foreground">
                          {product.reorderThreshold}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => setModal({ kind: 'restock', product })}
                            className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground press"
                          >
                            Restock
                          </button>
                          <button
                            type="button"
                            onClick={() => setModal({ kind: 'adjust', product })}
                            className="rounded-md border border-border px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground press"
                          >
                            Adjust
                          </button>
                          <button
                            type="button"
                            onClick={() => setModal({ kind: 'wastage', product })}
                            className="rounded-md border border-border px-2 py-0.5 text-xs text-destructive hover:text-destructive press"
                          >
                            Wastage
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>

          {/* Recent movements */}
          <section>
            <h2 className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
              Recent movements
            </h2>
            {movements.length === 0 ? (
              <div className="rounded-2xl bg-surface p-8 text-center ring-1 ring-border elevate">
                <p className="text-sm text-muted-foreground">No movements recorded yet.</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl bg-surface ring-1 ring-border elevate">
                <div className="grid grid-cols-[1fr_5rem_5rem_5rem_auto] gap-3 border-b border-border px-5 py-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  <span>Date</span>
                  <span>Type</span>
                  <span>Change</span>
                  <span>After</span>
                  <span>Notes</span>
                </div>
                <div className="divide-y divide-border">
                  {movements.map((m) => (
                    <div
                      key={m.id}
                      className="grid grid-cols-[1fr_5rem_5rem_5rem_auto] items-center gap-3 px-5 py-3 text-sm"
                    >
                      <span className="num text-muted-foreground text-xs">
                        {new Date(m.createdAt).toLocaleString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className={`text-xs font-medium ${movementTypeClass(m.movementType)}`}>
                        {movementTypeLabel(m.movementType)}
                      </span>
                      <span
                        className={`num text-xs font-medium ${m.changeQty > 0 ? 'text-success' : 'text-destructive'}`}
                      >
                        {m.changeQty > 0 ? '+' : ''}
                        {m.changeQty}
                      </span>
                      <span className="num text-xs text-muted-foreground">{m.qtyAfter}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {m.notes ?? (m.referenceKind === 'pos_transaction' ? 'POS sale' : '—')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {/* Modals */}
      {modal.kind === 'restock' && (
        <RestockModal
          product={modal.product}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={handleSaved}
        />
      )}
      {modal.kind === 'adjust' && (
        <AdjustModal
          product={modal.product}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={handleSaved}
        />
      )}
      {modal.kind === 'wastage' && (
        <WastageModal
          product={modal.product}
          onClose={() => setModal({ kind: 'none' })}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
